import * as React from 'react';
import { useState } from 'react';
import { observer } from 'mobx-react';

import { UitkTertiaryButton } from 'uitk-react-button';
import { UitkText } from 'uitk-react-text';

import { BaseTravelerOptions, WizardMenu } from '.';
import { trackAnalytics } from 'utility';
import { useStores } from 'utility/hooks';
import { trackTravelersOptions } from 'utility/traveler-option-utils';
import { FlightsSearchFormTravelersOption } from 'types/search/wizard';
import { InteractableFieldComponentProps } from 'types/shared';

interface TravelerOptionsMenuFormProps {
  travelerLimitLabel: string;
  label: string;
  travelerOptions: FlightsSearchFormTravelersOption;
  cancelAction: string;
  onCancel: () => void;
  doneAction: string;
  handleDone: () => void;
  closeMenu?: () => void;
  doneActionAccessibility?: string;
  cancelActionAccessibility?: string;
}

const TravelerOptionsMenuForm = observer((props: TravelerOptionsMenuFormProps) => {
  const {
    travelerLimitLabel,
    label,
    travelerOptions,
    cancelAction,
    onCancel,
    doneAction,
    handleDone,
    doneActionAccessibility,
    cancelActionAccessibility
  } = props;

  return (
    <form className="all-x-padding-three l-traveler-options-width" data-test-id="traveler-options">
      <UitkText size={300} className="all-b-margin-three">
        {travelerLimitLabel}
      </UitkText>
      <fieldset>
        <legend className="is-visually-hidden"> {label}</legend>
        <BaseTravelerOptions travelerOptions={travelerOptions} />
      </fieldset>
      <div className="uitk-grid">
        <div className="uitk-cell all-cell-1-2 uitk-type-center" data-test-id="traveler-options-cancel-button">
          <UitkTertiaryButton text={cancelAction} onClick={onCancel} aria-label={cancelActionAccessibility} />
        </div>
        <div className="uitk-cell all-cell-1-2 uitk-type-center" data-test-id="traveler-options-done-button">
          <UitkTertiaryButton text={doneAction} onClick={handleDone} aria-label={doneActionAccessibility} />
        </div>
      </div>
    </form>
  );
});

export const TravelerOptionsMenu = observer(({ onFieldInteraction }: InteractableFieldComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { searchFormStore, analytics } = useStores();
  const travelerOptions = searchFormStore.getSearchOption(
    'FlightsSearchFormTravelersOption'
  ) as FlightsSearchFormTravelersOption;
  const { hasTravelerErrors } = searchFormStore;
  const {
    cancelAction,
    doneAction,
    label,
    travelerLimitLabel,
    doneActionAccessibility,
    cancelActionAccessibility
  } = travelerOptions;
  const { icon } = travelerOptions;
  const loading = searchFormStore.searchForm.__typename === 'FlightsLoadingSearchForm';

  React.useEffect(() => {
    if (!isOpen && hasTravelerErrors) {
      setIsOpen(true);
    }
  }, [hasTravelerErrors, isOpen]);

  const revertChanges = () => {
    travelerOptions.currentErrors = [];
    searchFormStore.revertToInitialTravelerOptions();
    trackAnalytics(travelerOptions.cancelAnalytics, analytics);
  };

  const setInitialTravelerOptions = () => {
    searchFormStore.setInitialTravelerOptions();
    trackAnalytics(travelerOptions.openAnalytics, analytics);
  };

  const toggleMenu = () => {
    if (!isOpen) {
      setInitialTravelerOptions();
    }

    if (isOpen && searchFormStore.hasTravelerErrors) {
      return;
    }

    onFieldInteraction && onFieldInteraction();

    setIsOpen(!isOpen);
  };

  const submitChanges = () => {
    travelerOptions.currentErrors = [];
    searchFormStore.areTravelerInputsValid(analytics);

    if (isOpen && !searchFormStore.hasTravelerErrors) {
      searchFormStore.cleanUpInitialTravelersOptions();
      trackAnalytics(travelerOptions.applyAnalytics, analytics);
      trackTravelersOptions(travelerOptions, analytics);
    } else {
      searchFormStore.focusErrorSummaryInTravelers = true;
    }
  };

  const onCancel = () => {
    revertChanges();
    toggleMenu();
  };

  const handleDone = () => {
    submitChanges();
    toggleMenu();
  };

  return (
    <WizardMenu
      label={searchFormStore.travelersLabel}
      isOpen={isOpen}
      labelIcon={icon.id}
      onCloseCallback={submitChanges}
      toggleMenu={toggleMenu}
      containerWidth={265}
      dataTestId="flights-travelers-options-toggle"
    >
      {!loading && (
        <>
          <TravelerOptionsMenuForm
            handleDone={handleDone}
            onCancel={onCancel}
            doneAction={doneAction}
            cancelAction={cancelAction}
            travelerOptions={travelerOptions}
            label={label}
            travelerLimitLabel={travelerLimitLabel}
            doneActionAccessibility={doneActionAccessibility}
            cancelActionAccessibility={cancelActionAccessibility}
          />
        </>
      )}
    </WizardMenu>
  );
});
